<template>
	<div class="agreement-viewer">
		<div v-if="expiredMessage" class="agreement-viewer__expired" role="status">
			<div class="expired-document">
				<div class="expired-document__mockup" aria-hidden="true">
					<div class="expired-document__brand"></div>
					<div class="expired-document__title"></div>
					<div class="expired-document__subtitle"></div>
					<div class="expired-document__section"></div>
					<div class="expired-document__line wide"></div>
					<div class="expired-document__line"></div>
					<div class="expired-document__line short"></div>
					<div class="expired-document__cards"><span></span><span></span></div>
				</div>
				<div class="expired-document__message">{{ expiredMessage }}</div>
			</div>
		</div>
		<agreement-renderer
			v-else-if="agreement"
			ref="AgreementRenderer"
			:agreement="agreement"
			:signer="signer"
			:viewer-service="viewerService"
			:appearance="effectiveAppearance"
			:in-frame="inFrame"
			@print="printAgreement"
			@finalize="submitFinalized"
			:show-finish-warning="showWarning"
		></agreement-renderer>
		<div v-else class="agreement-viewer__loading">
			<v-progress-circular indeterminate color="primary" :size="36" :width="3" />
		</div>
	</div>
</template>

<script>
	import AgreementRenderer from '@/modules/agreements/AgreementRenderer';
	import AgreementViewerService from '@/modules/agreements/viewer/AgreementViewerService';
	import PublicAgreementViewerService from '@/modules/agreements/viewer/PublicAgreementViewerService';
	import { expiredAgreementMessage } from '@/modules/agreements/viewer/agreementExpiration';
	import AgreementMixin from '@/modules/agreements/schema/AgreementMixin';
	import { resolveDocumentBranding, resolvePrintBranding } from '@/utils/portalBranding';
	import { renderAllPdfEmbeds } from '@/modules/forms/v2/schema/pdfRenderer';
	import { Settings } from 'luxon';
	import { agreementStyles } from '@moxie/branding';

	export default {
		name: 'AgreementViewer',

		// publicSlug: a template's public link (/docs/:slug) — the agreement exists only on screen
		// until the visitor finishes. inFrame: shown in a website's embed.
		props: ['id', 'secretKey', 'print', 'publicSlug', 'inFrame'],
		mixins: [AgreementMixin],

		components: { AgreementRenderer },

		data: function() {
			return {
				agreement: null,
				signer: null,
				expiredMessage: null,
				finalized: false,
				showWarning: false,
				viewerService: this.publicSlug
					? new PublicAgreementViewerService(this.$store.state.domain.podUrl, this.$store.state.domain.accountId, this.publicSlug)
					: new AgreementViewerService(this.$store.state.domain.podUrl, this.id, this.secretKey),
				publicTemplateVersion: null,
				submissionKey: null,
				capturedBranding: null,
				printing: false,
				viewerFormatting: null,
			};
		},

		mounted() {
			this.$vuetify.theme.dark = false;
			this.$store.state.publicAgreement = !!this.publicSlug;
			this.getAgreement();

			if (!this.print && !this.inFrame) window.addEventListener('beforeunload', (event) => this.checkFinalized(event));
			window.addEventListener('beforeprint', this.preparePrint);
			window.addEventListener('afterprint', this.restoreScreenBranding);
		},

		beforeDestroy() {
			window.removeEventListener('beforeprint', this.preparePrint);
			window.removeEventListener('afterprint', this.restoreScreenBranding);
			this.$store.state.publicAgreement = false;
			this.$store.commit('setDocumentBranding', null);
			this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
			this.restoreViewerFormatting();
			document.documentElement.style.removeProperty('--print-page-color');
		},

		methods: {
			activateDocumentBranding() {
				this.$store.commit('setDocumentBranding', this.capturedBranding || false);
				this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
			},

			applyDocumentBranding(appearance = this.$vuetify.theme.dark ? 'dark' : 'light') {
				if (!this.capturedBranding || !this.agreement) return;
				this.$set(this.agreement, 'branding', resolveDocumentBranding(this.capturedBranding, appearance));
			},

			preparePrint() {
				if (this.capturedBranding) {
					this.printing = true;
					this.$vuetify.theme.dark = false;
					this.$set(this.agreement, 'branding', resolvePrintBranding(this.capturedBranding));
				}
				this.$store.state.documentPrinting = true;
				// Read by the print @page rule so the page color bleeds to the sheet edges.
				document.documentElement.style.setProperty('--print-page-color', agreementStyles(this.agreement).pageColor);
			},

			restoreScreenBranding() {
				this.$store.state.documentPrinting = false;
				if (!this.printing) return;
				this.printing = false;
				this.$store.state.eventBus.$emit('document-branding-changed', this.$store.state.darkMode);
				this.applyDocumentBranding();
			},

			// Native PDF from the API; the browser print dialog stays as the fallback.
			async printAgreement() {
				this.$store.commit('startLoading');
				try {
					const res = await this.viewerService.downloadPdf();
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
					link.setAttribute('download', `${(this.agreement.name || 'Agreement').replace(/[\\/:*?"<>|]+/g, ' ').trim()}.pdf`);
					document.body.appendChild(link);
					link.click();
					link.remove();
				} catch (err) {
					console.log('Native PDF unavailable, falling back to print', err);
					await this.browserPrint();
				} finally {
					this.$store.commit('stopLoading');
				}
			},

			async browserPrint() {
				this.preparePrint();
				await this.$nextTick();
				// Embedded PDF pages render as they scroll into view; draw them all first.
				await renderAllPdfEmbeds();
				window.print();
			},
			// print=1 (the PDF renderer): print branding in light mode, then tell the renderer
			// once fonts, embedded PDF pages and images have finished drawing.
			async signalPrintReady() {
				try {
					this.$vuetify.theme.dark = false;
					this.preparePrint();
					await this.loadFonts(this.agreement);
					await this.$nextTick();
					await this.until(() => !this.$refs.AgreementRenderer || !this.$refs.AgreementRenderer.packageOffersLoading);
					await renderAllPdfEmbeds();
					await document.fonts.ready;
					await this.imagesLoaded();
					window.__PRINT_READY__ = true;
				} catch (err) {
					window.__PRINT_ERROR__ = String(err.message || err);
				}
			},

			until(predicate, timeoutMs = 20000) {
				return new Promise((resolve) => {
					const started = Date.now();
					const check = () => (predicate() || Date.now() - started > timeoutMs ? resolve() : setTimeout(check, 100));
					check();
				});
			},

			imagesLoaded(timeoutMs = 15000) {
				const pending = [...document.images]
					.filter((img) => !img.complete)
					.map(
						(img) =>
							new Promise((resolve) => {
								img.addEventListener('load', resolve, { once: true });
								img.addEventListener('error', resolve, { once: true });
							})
					);
				return Promise.race([Promise.all(pending), new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
			},

			checkFinalized: function(event) {
				if (!this.finalized) {
					event.preventDefault();
					event.returnValue = false;
					setTimeout(() => (this.showWarning = true), 1000);
				}
			},

			// Once signed, the server freezes the locale and time zone the document renders in, so
			// every viewer (and the PDF renderer) sees identical dates and amounts. Must run before
			// the agreement is assigned so token computeds pick it up.
			applyRenderContext(context) {
				if (!context) return;
				if (!this.viewerFormatting) {
					this.viewerFormatting = {
						locale: Settings.defaultLocale,
						zone: Settings.defaultZoneName,
						storeLocale: this.$store.state.locale,
					};
				}
				Settings.defaultLocale = context.locale;
				Settings.defaultZoneName = context.timeZone;
				this.$store.state.locale = context.locale;
			},

			restoreViewerFormatting() {
				if (!this.viewerFormatting) return;
				Settings.defaultLocale = this.viewerFormatting.locale;
				Settings.defaultZoneName = this.viewerFormatting.zone;
				this.$store.state.locale = this.viewerFormatting.storeLocale;
				this.viewerFormatting = null;
			},

			getAgreement: function() {
				this.viewerService
					.getAgreement()
					.then((res) => {
						this.applyRenderContext(res.data.agreement.renderContext);
						this.agreement = res.data.agreement;
						this.capturedBranding = this.agreement.branding;
						this.activateDocumentBranding();
						this.applyDocumentBranding();
						this.signer = res.data.signer;
						this.$store.state.clientFields = res.data.clientFields;
						this.$store.state.contactFields = res.data.contactFields;
						this.$store.state.pipelineFields = res.data.pipelineFields;
						if (this.publicSlug) {
							this.publicTemplateVersion = res.data.templateVersion;
							this.submissionKey = this.publicSubmissionKey();
						}
						if (this.print) {
							this.signalPrintReady();
						} else {
							this.loadFonts(this.agreement);
						}
					})
					.catch((err) => {
						if (this.print) {
							window.__PRINT_ERROR__ = err.response ? `HTTP ${err.response.status}` : String(err.message || err);
							return;
						}
						if (err.response && err.response.status === 410) {
							this.expiredMessage = expiredAgreementMessage(err);
							this.finalized = true;
							return;
						}
						this.$router.push('/NotFound');
					});
			},

			async submitFinalized({ packageOffer = null } = {}) {
				if (this.publicSlug) return this.submitPublic({ packageOffer });
				this.$store.commit('startLoading');

				// The payment block explains any reason signing can't continue.
				if (this.$store.state.agreementPaymentElement) {
					const paid = await this.$store.state.agreementPaymentElement.processPayment();
					if (!paid) {
						this.$store.commit('stopLoading');
						return;
					}
				}

				this.viewerService
					.finalizeAgreementFormData(
						this.capturedBranding ? { ...this.agreement, branding: this.capturedBranding } : this.agreement,
						packageOffer
					)
					.then((res) => {
						this.applyRenderContext(res.data.agreement.renderContext);
						this.agreement = res.data.agreement;
						this.capturedBranding = this.agreement.branding;
						this.activateDocumentBranding();
						this.applyDocumentBranding();
						this.signer = res.data.signer;
						this.$refs.AgreementRenderer.handlePostSignature();
						this.finalized = true;
						this.showWarning = false;
					})
					.catch((err) => {
						const message = err.response && err.response.data && err.response.data.message;
						this.$store.commit('error', message || this.$t('agreement-payment.submit-failed'));
						this.$refs.AgreementRenderer.reset();
					})
					.finally(() => this.$store.commit('stopLoading'));
			},

			// Public link Finish: card first (when the agreement takes payment), then the server
			// charges it and only then creates the client and agreement. Card checks such as 3-D
			// Secure come back as REQUIRES_ACTION and resubmit once the bank confirms.
			async submitPublic({ packageOffer = null } = {}, paymentIntentId = null) {
				const paymentElement = this.$store.state.agreementPaymentElement;
				this.$store.commit('startLoading');
				try {
					let payment = { required: false };
					if (!paymentIntentId && paymentElement) {
						payment = await paymentElement.preparePublicPayment();
						if (!payment) return;
					}
					const agreement = this.capturedBranding ? { ...this.agreement, branding: this.capturedBranding } : this.agreement;
					const { data } = await this.viewerService.submit(agreement, {
						packageOffer,
						templateVersion: this.publicTemplateVersion,
						submissionKey: this.submissionKey,
						paymentMethodId: payment.paymentMethodId || null,
						paymentIntentId,
						amount: payment.amount || null,
					});
					if (data.outcome === 'REQUIRES_ACTION') {
						this.$store.commit('stopLoading');
						if (await paymentElement.completePublicAction(data.clientSecret)) {
							await this.submitPublic({ packageOffer }, data.paymentIntentId);
						}
						return;
					}
					this.finalized = true;
					this.showWarning = false;
					this.forgetPublicSubmissionKey();
					const signedPath = `/document/${data.token}${this.inFrame ? '?inFrame=true' : ''}`;
					this.$refs.AgreementRenderer.handlePostSignature(() => this.$router.replace(signedPath));
				} catch (err) {
					const message = err.response && err.response.data && err.response.data.message;
					this.$store.commit('error', message || this.$t('agreement-payment.submit-failed'));
					this.$refs.AgreementRenderer.reset();
				} finally {
					this.$store.commit('stopLoading');
				}
			},

			// One id per visitor submission, kept across a reload so a retry after a dropped
			// response finishes the same agreement instead of charging again. Storage can be
			// blocked inside an embed; the id then lasts for this page view.
			publicSubmissionKey() {
				try {
					const saved = JSON.parse(localStorage.getItem(this.publicKeyStorage) || 'null');
					if (saved && saved.templateVersion === this.publicTemplateVersion && saved.key) return saved.key;
				} catch (e) {
					// Storage unavailable
				}
				const key = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
				try {
					localStorage.setItem(this.publicKeyStorage, JSON.stringify({ templateVersion: this.publicTemplateVersion, key }));
				} catch (e) {
					// Storage unavailable
				}
				return key;
			},

			forgetPublicSubmissionKey() {
				try {
					localStorage.removeItem(this.publicKeyStorage);
				} catch (e) {
					// Storage unavailable
				}
			},
		},

		computed: {
			publicKeyStorage() {
				return `moxie.publicAgreement.${this.$store.state.domain.accountId}.${this.publicSlug}`;
			},
			effectiveAppearance() {
				return this.$vuetify.theme.dark ? 'dark' : 'light';
			},
		},

		watch: {
			'$vuetify.theme.dark'() {
				if (!this.printing) this.applyDocumentBranding();
			},
		},
	};
</script>

<style scoped lang="scss">
	.agreement-viewer {
		min-height: var(--vh);
		background: var(--v-gray_10-base);

		&__loading {
			min-height: var(--vh);
			display: flex;
			align-items: center;
			justify-content: center;
		}

		&__expired {
			min-height: var(--vh);
			display: grid;
			place-items: center;
			padding: 40px 20px;
		}
	}

	.expired-document {
		position: relative;
		width: min(680px, 88vw);
		min-height: min(760px, calc(var(--vh) - 80px));
		border-radius: 12px;
		background: var(--portal-surface, #fff);
		box-shadow: 0 24px 70px rgba(25, 28, 38, 0.18);
		overflow: hidden;

		&__mockup {
			padding: clamp(36px, 7vw, 72px);
			opacity: 0.32;
		}

		&__brand,
		&__title,
		&__subtitle,
		&__section,
		&__line,
		&__cards span {
			border-radius: 999px;
			background: var(--portal-muted, #9ca3af);
		}

		&__brand { width: 78px; height: 28px; margin-bottom: 64px; background: var(--v-primary-base); }
		&__title { width: 68%; height: 18px; margin-bottom: 14px; }
		&__subtitle { width: 42%; height: 10px; margin-bottom: 72px; }
		&__section { width: 28%; height: 12px; margin-bottom: 22px; background: var(--v-primary-base); }
		&__line { width: 76%; height: 8px; margin-bottom: 14px; }
		&__line.wide { width: 100%; }
		&__line.short { width: 54%; }
		&__cards { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-top: 64px; }
		&__cards span { height: 150px; border-radius: 14px; }

		&__message {
			position: absolute;
			top: 50%;
			left: 50%;
			width: max-content;
			max-width: calc(100% - 40px);
			transform: translate(-50%, -50%);
			padding: 18px 26px;
			border: 1px solid var(--portal-border, #e5e7eb);
			border-radius: var(--portal-radius-md, 16px);
			background: var(--portal-surface, #fff);
			box-shadow: 0 16px 45px rgba(25, 28, 38, 0.22);
			color: var(--portal-text, #1f2937);
			font-size: clamp(18px, 3vw, 24px);
			font-weight: 700;
			line-height: 1.3;
			text-align: center;
		}
	}

	@media (max-width: 600px) {
		.agreement-viewer__expired { padding: 20px 12px; }
		.expired-document { width: 100%; min-height: calc(var(--vh) - 40px); }
		.expired-document__mockup { padding: 34px 26px; }
		.expired-document__cards { grid-template-columns: 1fr; }
		.expired-document__cards span + span { display: none; }
	}
</style>
